/* oxlint-disable */
import {
  useCallback, useEffect, useState, useRef,
} from 'react';

import {
  getLiveGamesAction,
  getTopGamesOver1000Action,
} from '@/app/(main)/casino/action';

import { LiveGame } from '@/features/v2/casino/interfaces/game.interface';
import { getFakeDataByType } from '@/features/v2/casino/services/fakeData';

import { useLiveCache } from './useLiveCache';

type FilterType = 'live' | 'highRollers';

export const useLiveBets = (instanceId?: string) => {
  const [games, setGames] = useState<LiveGame[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterType>('highRollers');
  const [isFakeData, setIsFakeData] = useState(false);
  const isInitialized = useRef(false);

  const liveCache = useLiveCache('live', instanceId);
  const highRollersCache = useLiveCache('highRollers', instanceId);

  const getCurrentCache = useCallback((filter: FilterType) => {
    switch (filter) {
      case 'live':
        return liveCache;
      case 'highRollers':
        return highRollersCache;
      default:
        return highRollersCache;
    }
  }, [liveCache, highRollersCache]);

  const fetchGamesByFilter = useCallback(async (filter: FilterType): Promise<LiveGame[]> => {
    try {
      if (filter === 'highRollers') {
        return await getTopGamesOver1000Action();
      }

      return await getLiveGamesAction();
    } catch (err) {
      console.warn(`Error fetching ${filter} games:`, err);
      throw err;
    }
  }, []);

  const loadGames = useCallback(async (filter: FilterType) => {
    const currentCache = getCurrentCache(filter);

    const cached = currentCache.getCachedData();
    if (cached && !cached.isLoading) {
      setGames(cached.data);
      setIsFakeData(false);
      setIsLoading(false);
      setError(cached.error);

      currentCache.scheduleRefresh(() => {
        loadGames(filter);
      });
      return;
    }

    const fakeData = getFakeDataByType(filter);
    setGames(fakeData);
    setIsFakeData(true);
    setIsLoading(false);
    setError(null);

    try {
      const gamesData = await fetchGamesByFilter(filter);
      setGames(gamesData);
      setIsFakeData(false);
      currentCache.setCachedData(gamesData, false, null);

      currentCache.scheduleRefresh(() => {
        loadGames(filter);
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      currentCache.setCachedData([], false, errorMessage);
    }
  }, [fetchGamesByFilter, getCurrentCache]);

  const changeFilter = useCallback((newFilter: FilterType) => {
    setActiveFilter(newFilter);
    loadGames(newFilter);
  }, []);

  const refetch = useCallback(() => {
    loadGames(activeFilter);
  }, []);

  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      loadGames(activeFilter);
    }
  }, [activeFilter]);

  useEffect(() => {
    return () => {
      liveCache.clearTimeout();
      highRollersCache.clearTimeout();
    };
  }, [liveCache, highRollersCache]);

  return {
    games,
    isLoading,
    error,
    activeFilter,
    isFakeData,
    changeFilter,
    refetch,
  };
};
